import { useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'

import AuthHeader from '../features/auth/AuthHeader'
import EyeIcon from '../components/EyeIcon'
import { reilmAPI } from '../services/apiClient'

const LockBadge = () => (
  <div className="relative w-[88px] h-[88px] rounded-full bg-[#e4f3da] flex items-center justify-center">
    <svg width="36" height="36" viewBox="0 0 24 24" fill="none">
      <rect x="5" y="10" width="14" height="10" rx="2" stroke="#9dbb8f" strokeWidth="1.8" />
      <path d="M8 10V7a4 4 0 018 0v3" stroke="#9dbb8f" strokeWidth="1.8" />
      <circle cx="12" cy="15" r="1.5" fill="#9dbb8f" />
    </svg>
    <span className="absolute -top-1 -left-1 text-brand-green text-[18px]">✦</span>
  </div>
)

const ResetPassword = () => {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')

  const [password, setPassword] = useState('')
  const [confirm, setConfirm] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters')
      return
    }
    if (password !== confirm) {
      setError('Passwords do not match')
      return
    }
    setSubmitting(true)
    try {
      await reilmAPI.resetPassword({ token, newPassword: password })
      // M3.6.1: confirm the change instead of dropping the user back on /login unannounced.
      navigate('/password-updated', { replace: true })
    } catch (err) {
      setError(
        err?.response?.data?.error?.message ||
          'This reset link is invalid or has expired. Please request a new one.'
      )
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-pageFill flex flex-col">
      <AuthHeader />

      {/* M3.3: 40px from the navbar to the content, all breakpoints. */}
      <main className="flex-1 flex items-start justify-center px-6 pt-[40px]">
        <form
          onSubmit={handleSubmit}
          className="w-[576px] flex flex-col items-center gap-8"
        >
          <LockBadge />

          <h1 className="text-[32px] leading-[40px] md:text-[40px] md:leading-[48px] font-extrabold text-brand-dark text-center w-full">
            New Password
          </h1>

          {!token && (
            <p className="text-[14px] text-red-600 font-medium text-center">
              This reset link is invalid.{' '}
              <Link to="/forgot-password" className="text-brand-green underline hover:text-brand-hover transition-colors">
                Request a new one
              </Link>
            </p>
          )}

          <div className="w-full flex flex-col gap-4">
            <div className="relative w-full">
              <input
                type={showPassword ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="New Password"
                className="w-full h-[64px] bg-white border border-[#cbdcc3] rounded-full pl-6 pr-14 text-[16px] text-brand-dark placeholder:text-[#909090] outline-none focus:border-brand-green"
              />
              <button
                type="button"
                onClick={() => setShowPassword((s) => !s)}
                aria-label={showPassword ? 'Hide password' : 'Show password'}
                className="absolute right-5 top-1/2 -translate-y-1/2"
              >
                <EyeIcon open={showPassword} />
              </button>
            </div>

            <div className="relative w-full">
              <input
                type={showConfirm ? 'text' : 'password'}
                required
                autoComplete="new-password"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                placeholder="Confirm Password"
                className="w-full h-[64px] bg-white border border-[#cbdcc3] rounded-full pl-6 pr-14 text-[16px] text-brand-dark placeholder:text-[#909090] outline-none focus:border-brand-green"
              />
              <button
                type="button"
                onClick={() => setShowConfirm((s) => !s)}
                aria-label={showConfirm ? 'Hide password' : 'Show password'}
                className="absolute right-5 top-1/2 -translate-y-1/2"
              >
                <EyeIcon open={showConfirm} />
              </button>
            </div>
          </div>

          {error && (
            <p className="text-[14px] text-red-600 font-medium text-center">
              {error}
            </p>
          )}

          <button
            type="submit"
            disabled={submitting || !token}
            className="w-full max-w-[264px] h-[56px] rounded-full bg-brand-primary hover:bg-brand-hover text-white text-[16px] font-semibold disabled:opacity-60 transition-colors"
          >
            {submitting ? 'Updating…' : 'Update Password'}
          </button>
          {/* M3.6.2: no signup cross-link on the password-change pages. */}
        </form>
      </main>
    </div>
  )
}

export default ResetPassword
